import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ContactBar from "@/components/feature/ContactBar";
import SEOMeta from "@/components/base/SEOMeta";

const siteUrl = import.meta.env.VITE_SITE_URL || "";

const pageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${siteUrl}/termos-de-uso`,
  "name": "Termos de Uso - RCRED Negócios Financeiros",
  "description": "Termos de Uso da RCRED Negócios Financeiros. Conheça as condições de utilização do nosso site, licenças, limitações e responsabilidades.",
  "url": `${siteUrl}/termos-de-uso`,
  "inLanguage": "pt-BR",
  "isPartOf": {
    "@id": `${siteUrl}/#website`
  }
};

export default function TermsOfService() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEOMeta jsonLd={[pageSchema]} id="terms-schema" />
      <Navbar />

      <main className="flex-1 pt-24 md:pt-28 pb-16 md:pb-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <article className="max-w-4xl mx-auto">
            <header className="mb-10 md:mb-12">
              <h1 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-3">
                Termos de Uso
              </h1>
              <p className="text-sm text-foreground-500">
                Última atualização: 11 de julho de 2026
              </p>
            </header>

            <div className="prose prose-foreground max-w-none space-y-6">
              <section>
                <h2 className="text-2xl font-heading text-foreground-950 mb-4">
                  1. Termos
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Ao acessar ao site{" "}
                  <a href="https://rcrednegocios.com.br" className="text-primary-600 hover:text-primary-700 underline underline-offset-2">
                    RCred Negócios
                  </a>
                  , concorda em cumprir estes termos de serviço, todas as leis e regulamentos aplicáveis e concorda que é responsável pelo cumprimento de todas as leis locais aplicáveis. Se você não concordar com algum desses termos, está proibido de usar ou acessar este site. Os materiais contidos neste site são protegidos pelas leis de direitos autorais e marcas comerciais aplicáveis.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-heading text-foreground-950 mb-4">
                  2. Uso de Licença
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  É concedida permissão para baixar temporariamente uma cópia dos materiais (informações ou software) no site RCred Negócios, apenas para visualização transitória pessoal e não comercial. Esta é a concessão de uma licença, não uma transferência de título e, sob esta licença, você não pode:
                </p>
                <ol className="list-decimal pl-5 space-y-2 text-foreground-700 leading-relaxed mt-3">
                  <li>modificar ou copiar os materiais;</li>
                  <li>usar os materiais para qualquer finalidade comercial ou para exibição pública (comercial ou não comercial);</li>
                  <li>tentar descompilar ou fazer engenharia reversa de qualquer software contido no site RCred Negócios;</li>
                  <li>remover quaisquer direitos autorais ou outras notações de propriedade dos materiais; ou</li>
                  <li>transferir os materiais para outra pessoa ou &apos;espelhe&apos; os materiais em qualquer outro servidor.</li>
                </ol>
                <p className="text-foreground-700 leading-relaxed mt-3">
                  Esta licença será automaticamente rescindida se você violar alguma dessas restrições e poderá ser rescindida por RCred Negócios a qualquer momento. Ao encerrar a visualização desses materiais ou após o término desta licença, você deve apagar todos os materiais baixados em sua posse, seja em formato eletrônico ou impresso.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-heading text-foreground-950 mb-4">
                  3. Isenção de responsabilidade
                </h2>
                <ol className="list-decimal pl-5 space-y-2 text-foreground-700 leading-relaxed">
                  <li>
                    Os materiais no site da RCred Negócios são fornecidos &apos;como estão&apos;. RCred Negócios não oferece garantias, expressas ou implícitas, e, por este meio, isenta e nega todas as outras garantias, incluindo, sem limitação, garantias implícitas ou condições de comercialização, adequação a um fim específico ou não violação de propriedade intelectual ou outra violação de direitos.
                  </li>
                  <li>
                    Além disso, o RCred Negócios não garante ou faz qualquer representação relativa à precisão, aos resultados prováveis ou à confiabilidade do uso dos materiais em seu site ou de outra forma relacionado a esses materiais ou em sites vinculados a este site.
                  </li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-heading text-foreground-950 mb-4">
                  4. Limitações
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Em nenhum caso o RCred Negócios ou seus fornecedores serão responsáveis por quaisquer danos (incluindo, sem limitação, danos por perda de dados ou lucro ou devido a interrupção dos negócios) decorrentes do uso ou da incapacidade de usar os materiais em RCred Negócios, mesmo que RCred Negócios ou um representante autorizado da RCred Negócios tenha sido notificado oralmente ou por escrito da possibilidade de tais danos. Como algumas jurisdições não permitem limitações em garantias implícitas, ou limitações de responsabilidade por danos consequentes ou incidentais, essas limitações podem não se aplicar a você.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-heading text-foreground-950 mb-4">
                  5. Precisão dos materiais
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Os materiais exibidos no site da RCred Negócios podem incluir erros técnicos, tipográficos ou fotográficos. RCred Negócios não garante que qualquer material em seu site seja preciso, completo ou atual. RCred Negócios pode fazer alterações nos materiais contidos em seu site a qualquer momento, sem aviso prévio. No entanto, RCred Negócios não se compromete a atualizar os materiais.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-heading text-foreground-950 mb-4">
                  6. Links
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  O RCred Negócios não analisou todos os sites vinculados ao seu site e não é responsável pelo conteúdo de nenhum site vinculado. A inclusão de qualquer link não implica endosso por RCred Negócios do site. O uso de qualquer site vinculado é por conta e risco do usuário.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-heading text-foreground-950 mt-10 mb-4">
                  Modificações
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  O RCred Negócios pode revisar estes termos de serviço do site a qualquer momento, sem aviso prévio. Ao usar este site, você concorda em ficar vinculado à versão atual desses termos de serviço.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-heading text-foreground-950 mt-10 mb-4">
                  Lei aplicável
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Estes termos e condições são regidos e interpretados de acordo com as leis do RCred Negócios e você se submete irrevogavelmente à jurisdição exclusiva dos tribunais naquele estado ou localidade.
                </p>
              </section>
            </div>
          </article>
        </div>
      </main>

      <Footer />
      <ContactBar />
      <div className="h-12 md:h-0"></div>
    </div>
  );
}